import { useState, useEffect, Suspense, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Badge, Stack, Spinner } from 'react-bootstrap';
import { CaretLeft, CaretRight, XLg } from 'react-bootstrap-icons';
import { Canvas } from '@react-three/fiber';
import { Stage } from '@react-three/drei';
import axios from 'axios';
import { Model } from '../Model/Model';
import { bodyMap } from '../Model/BodyMap';
import { states } from '../QuoteEstimate/constants';

export function PostLeadModal({ show, onHide, idToken, onLeadPosted }) {
  const ref = useRef();
  const [showFront, setShowFront] = useState(true);
  const [selectedBodyParts, setSelectedBodyParts] = useState([]);
  const [injuryDetails, setInjuryDetails] = useState({});
  const [accidentTypes, setAccidentTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    state: '',
    accidentTypeId: '',
    price: ''
  });

  // Fetch accident types on mount
  useEffect(() => {
    if (show && idToken) {
      axios.get(
        `${process.env.REACT_APP_CORE_API}/accidenttype`,
        { headers: { Authorization: idToken } }
      ).then(res => {
        setAccidentTypes(res.data.value || res.data || []);
      }).catch(err => {
        console.error('Failed to fetch accident types:', err);
      });
    }
  }, [show, idToken]);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const removeBodyPart = (materialName) => {
    setSelectedBodyParts(selectedBodyParts.filter(p => p !== materialName));
    const newDetails = { ...injuryDetails };
    delete newDetails[materialName];
    setInjuryDetails(newDetails);
  };

  const getBodyPartName = (materialName) => {
    const part = bodyMap.get(materialName);
    return part ? part.bodyPart : materialName;
  };

  const updateInjuryDetail = (materialName, field, value) => {
    setInjuryDetails({
      ...injuryDetails,
      [materialName]: {
        ...injuryDetails[materialName],
        [field]: value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);

      // Create the lead with its injuries in one request
      const injuries = selectedBodyParts.map(materialName => {
        const part = bodyMap.get(materialName);
        const details = injuryDetails[materialName] || {};
        return {
          bodyPart: part?.bodyPart || materialName,
          injuryType: details.injuryType || 'Injury',
          severity: details.severity || 1
        };
      });

      const leadPayload = {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        state: form.state,
        accidentTypeId: form.accidentTypeId,
        price: parseFloat(form.price),
        status: 'ForSale',
        injuries
      };

      const created = await axios.post(
        `${process.env.REACT_APP_CORE_API}/lead`,
        leadPayload,
        { headers: { Authorization: idToken } }
      );

      if (photo) {
        const data = new FormData();
        data.append('photo', photo);
        try {
          await axios.post(
            `${process.env.REACT_APP_CORE_API}/lead/${created.data.id}/photo`,
            data,
            { headers: { Authorization: idToken } }
          );
        } catch (err) {
          const reason = err.response?.data?.detail || err.response?.data || 'Photo upload failed';
          alert(`Lead posted, but the photo was not saved: ${reason}. You can add one from your leads list.`);
        }
      }

      // Reset form
      setForm({
        firstName: '',
        lastName: '',
        phone: '',
        state: '',
        accidentTypeId: '',
        price: ''
      });
      setSelectedBodyParts([]);
      setInjuryDetails({});
      setPhoto(null);
      setPhotoError(null);
      onHide();
      onLeadPosted?.();
    } catch (err) {
      console.error('Failed to post lead:', err);
      alert('Failed to post lead. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoPick = (file) => {
    setPhotoError(null);
    if (!file) { setPhoto(null); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoError('Photo must be a JPEG, PNG, or WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB');
      return;
    }
    setPhoto(file);
  };

  const handleClose = () => {
    setSelectedBodyParts([]);
    setInjuryDetails({});
    setPhoto(null);
    setPhotoError(null);
    setShowFront(true);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Post a New Lead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          {/* 3D Model Section */}
          <Col md={6}>
            <div className="text-center mb-3">
              <h6 className="text-muted">Click to select injured body parts (up to 4)</h6>
            </div>

            <div style={{ height: '400px', position: 'relative' }}>
              <Stack direction="horizontal" gap={2} className="h-100 justify-content-center">
                <button
                  type="button"
                  className="rotate-btn"
                  onClick={() => setShowFront(!showFront)}
                  aria-label="Rotate left"
                >
                  <CaretLeft size={30} />
                </button>

                <div style={{ height: '100%', width: '250px' }}>
                  <Canvas shadows dpr={[1, 2]} camera={{ fov: 50 }}>
                    <Suspense fallback={null}>
                      <Stage controls={ref} preset="rembrandt" intensity={2} contactShadow={false} environment="studio">
                        <Model
                          showFront={showFront}
                          selectedBodyParts={selectedBodyParts}
                          setSelectedBodyParts={setSelectedBodyParts}
                        />
                      </Stage>
                    </Suspense>
                  </Canvas>
                </div>

                <button
                  type="button"
                  className="rotate-btn"
                  onClick={() => setShowFront(!showFront)}
                  aria-label="Rotate right"
                >
                  <CaretRight size={30} />
                </button>
              </Stack>
            </div>

            {/* Selected body parts */}
            <div className="text-center mt-3">
              {selectedBodyParts.length > 0 ? (
                <span className="text-muted small mb-2 d-block">
                  {selectedBodyParts.length}/4 body parts selected
                </span>
              ) : (
                <p className="text-muted small">Tap on the body to select injured areas</p>
              )}
            </div>
          </Col>

          {/* Form Section */}
          <Col md={6}>
            <Form onSubmit={handleSubmit}>
              <h6 className="text-muted mb-3">Contact Information</h6>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>State</Form.Label>
                    <Form.Select
                      value={form.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((state, idx) => (
                        <option key={idx} value={state.abbreviation}>
                          {state.abbreviation} - {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-4" />

              <h6 className="text-muted mb-3">Accident Details</h6>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Accident Type</Form.Label>
                    <Form.Select
                      value={form.accidentTypeId}
                      onChange={(e) => handleChange('accidentTypeId', e.target.value)}
                      required
                    >
                      <option value="">Select Accident Type</option>
                      {accidentTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Price ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      value={form.price}
                      onChange={(e) => handleChange('price', e.target.value)}
                      placeholder="e.g. 500"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Photo <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handlePhotoPick(e.target.files[0])}
                  isInvalid={!!photoError}
                />
                <Form.Control.Feedback type="invalid">{photoError}</Form.Control.Feedback>
                {photo && !photoError && (
                  <Form.Text className="text-muted">{photo.name}</Form.Text>
                )}
              </Form.Group>

              {selectedBodyParts.length === 0 ? (
                <div className="alert alert-info small">
                  Please select at least one injured body part on the 3D model
                </div>
              ) : (
                <>
                  <hr className="my-4" />
                  <h6 className="text-muted mb-3">Injury Details</h6>
                  {selectedBodyParts.map((materialName, idx) => (
                    <div key={idx} className="p-3 bg-light rounded mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <strong>{getBodyPartName(materialName)}</strong>
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex align-items-center gap-2" style={{ minWidth: '180px' }}>
                            <Form.Range
                              min={1}
                              max={10}
                              value={injuryDetails[materialName]?.severity || 1}
                              onChange={(e) => updateInjuryDetail(materialName, 'severity', parseInt(e.target.value))}
                              style={{ width: '120px' }}
                            />
                            <Badge bg={
                              (injuryDetails[materialName]?.severity || 1) >= 7 ? 'danger' :
                              (injuryDetails[materialName]?.severity || 1) >= 4 ? 'warning' : 'success'
                            }>
                              {injuryDetails[materialName]?.severity || 1}/10
                            </Badge>
                          </div>
                          <XLg
                            size={12}
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeBodyPart(materialName)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="qmi-btn-primary"
          onClick={handleSubmit}
          disabled={selectedBodyParts.length === 0 || submitting || !form.price}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Posting...
            </>
          ) : (
            'Post Lead'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
